type RecentVisitType = 'promo' | 'voucher';

export type PromoRecentVisit = { code: string; href: string; visitedAt: string };
export const PROMO_RECENT_VISITS_KEY = 'promo:recent-promo-visits:v1';
export const VOUCHER_RECENT_VISITS_KEY = 'promo:recent-voucher-visits:v1';
const LEGACY_RECENT_VISITS_KEY = 'promo:recent-visits:v1';
/** Same-window localStorage writes do not emit the browser `storage` event. */
export const PROMO_RECENT_VISITS_UPDATED_EVENT = 'promo-recent-visits-updated';

function getStorageKey(type: RecentVisitType): string {
  return type === 'promo' ? PROMO_RECENT_VISITS_KEY : VOUCHER_RECENT_VISITS_KEY;
}

function getLegacyVisits(type: RecentVisitType): Array<PromoRecentVisit> {
  try {
    const legacyVisits = JSON.parse(window.localStorage.getItem(LEGACY_RECENT_VISITS_KEY) || '[]') as Array<
      PromoRecentVisit & { type?: RecentVisitType }
    >;
    return legacyVisits
      .filter((visit) => visit.type === type)
      .map(({ code, href, visitedAt }) => ({ code, href, visitedAt }));
  } catch {
    return [];
  }
}

export function getPromoRecentVisits(type: RecentVisitType): Array<PromoRecentVisit> {
  try {
    const storageKey = getStorageKey(type);
    const storedVisits = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
    if (Array.isArray(storedVisits)) {
      return storedVisits;
    }
    return getLegacyVisits(type);
  } catch {
    return [];
  }
}

function recordRecentVisit(
  type: RecentVisitType,
  visit: Omit<PromoRecentVisit, 'visitedAt'>,
): Array<PromoRecentVisit> | undefined {
  try {
    const storageKey = getStorageKey(type);
    const existing = JSON.parse(window.localStorage.getItem(storageKey) || '[]') as Array<PromoRecentVisit>;
    const next = [
      { ...visit, visitedAt: new Date().toISOString() },
      ...existing.filter((item) => item.code !== visit.code),
    ].slice(0, 50);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(PROMO_RECENT_VISITS_UPDATED_EVENT, { detail: { type, visits: next } }));
    return next;
  } catch {
    // Storage is optional; navigation should never fail because it is unavailable.
  }
}

export function recordPromoVisit(visit: Omit<PromoRecentVisit, 'visitedAt'>) {
  return recordRecentVisit('promo', visit);
}

export function recordVoucherVisit(visit: Omit<PromoRecentVisit, 'visitedAt'>) {
  return recordRecentVisit('voucher', visit);
}
